/**
 * PillarBody — one pillar inside the matrix: its header (which expands the pillar's plot),
 * the plot row when open, then its metric rows. Split out of MetricMatrix on the CD-1
 * module budget when the header gained the expand affordance.
 */

import React from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { Cell, Pillar, RegistryMetric } from '../../types/metrics'
import MatrixRow, { type CellLookup } from './MatrixRow'

export interface PillarRows {
  pillar: Pillar
  metrics: RegistryMetric[]
}

/** The pillar's own row: a full-width button that expands its plot. */
function PillarHeader({
  pillar,
  span,
  open,
  onToggle,
}: {
  pillar: Pillar
  span: number
  open: boolean
  onToggle: () => void
}): React.JSX.Element {
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <tr data-testid={`pillar-${pillar}`} className="bg-bg2">
      <th colSpan={span} className="sticky left-0 border-y border-line p-0 text-left">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          data-testid={`plot-toggle-${pillar}`}
          className="flex w-full items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[.14em] text-mut hover:text-ink"
        >
          <Chevron size={11} aria-hidden className="flex-shrink-0" />
          {pillar}
          {/* Say what the click does, in the same words both ways. "plot the pillar" named
              our internal object rather than the thing on screen, which is a trend. */}
          <span className="ml-2 normal-case tracking-normal text-mut2">
            {open ? 'hide trend' : 'show trend'}
          </span>
        </button>
      </th>
    </tr>
  )
}

/** The header bar spans the whole table; the PLOT does not. The chart is measured against
 *  the period columns (plotAlign), so letting it run under the YTD column too would slide
 *  every point off the month it explains — an empty cell keeps the row square instead. */
function PlotRow({
  pillar,
  span,
  showYtd,
  plot,
}: {
  pillar: Pillar
  span: number
  showYtd: boolean
  plot: React.ReactNode
}): React.JSX.Element {
  return (
    <tr data-testid={`plot-row-${pillar}`}>
      <td colSpan={span} className="p-0">
        {plot}
      </td>
      {showYtd && <td className="border-l border-line bg-bg2/30 p-0" />}
    </tr>
  )
}

export default function PillarBody({
  group,
  periods,
  cellFor,
  open,
  onToggle,
  plot,
  showYtd,
  onDrill,
  onTrend,
}: {
  group: PillarRows
  periods: string[]
  cellFor: CellLookup
  open: boolean
  onToggle: () => void
  plot: React.ReactNode
  showYtd: boolean
  onDrill?: (cell: Cell) => void
  onTrend?: (metricId: string) => void
}): React.JSX.Element {
  return (
    <tbody>
      <PillarHeader
        pillar={group.pillar}
        span={periods.length + 1 + (showYtd ? 1 : 0)}
        open={open}
        onToggle={onToggle}
      />
      {open && <PlotRow pillar={group.pillar} span={periods.length + 1} showYtd={showYtd} plot={plot} />}
      {group.metrics.map((metric) => (
        <MatrixRow key={metric.id} metric={metric} periods={periods} cellFor={cellFor} showYtd={showYtd} onDrill={onDrill} onTrend={onTrend} />
      ))}
    </tbody>
  )
}
